import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `class TopK {
  /** @param k how many entries \`top()\` returns */
  constructor(private k: number) {}

  /** Record one occurrence of \`key\`. */
  add(key: string): void {
    // TODO
  }

  /** The k highest counts, highest first. Ties broken by key, ascending. */
  top(): { key: string; count: number }[] {
    // TODO
    return [];
  }
}
`;

const solution = `class TopK {
  private counts = new Map<string, number>();

  constructor(private k: number) {}

  add(key: string): void {
    this.counts.set(key, (this.counts.get(key) ?? 0) + 1);
  }

  top(): { key: string; count: number }[] {
    // A full sort is O(n log n) per query. Keeping the k best while scanning is
    // O(n log k), which for the usual case (a million endpoints, k = 10) is the
    // difference between a query you can run per request and one you cannot.
    //
    // The heap holds the SMALLEST of the current best at its root, so the test
    // for "does this beat what we have" is a single comparison against the root.
    const heap: { key: string; count: number }[] = [];

    const worseThan = (a: { key: string; count: number }, b: { key: string; count: number }) =>
      a.count !== b.count ? a.count < b.count : a.key > b.key;

    const swap = (i: number, j: number) => {
      const t = heap[i]!;
      heap[i] = heap[j]!;
      heap[j] = t;
    };

    const up = (i: number) => {
      while (i > 0) {
        const parent = (i - 1) >> 1;
        if (!worseThan(heap[i]!, heap[parent]!)) break;
        swap(i, parent);
        i = parent;
      }
    };

    const down = (i: number) => {
      for (;;) {
        const left = i * 2 + 1;
        const right = left + 1;
        let smallest = i;
        if (left < heap.length && worseThan(heap[left]!, heap[smallest]!)) smallest = left;
        if (right < heap.length && worseThan(heap[right]!, heap[smallest]!)) smallest = right;
        if (smallest === i) break;
        swap(i, smallest);
        i = smallest;
      }
    };

    for (const [key, count] of this.counts) {
      const entry = { key, count };
      if (heap.length < this.k) {
        heap.push(entry);
        up(heap.length - 1);
      } else if (heap.length > 0 && worseThan(heap[0]!, entry)) {
        heap[0] = entry;
        down(0);
      }
    }

    // The heap is not itself ordered, so the k survivors are sorted on the way
    // out. That is O(k log k), not O(n log n).
    return heap.sort((a, b) => (b.count - a.count) || (a.key < b.key ? -1 : 1));
  }
}`;

const harness = HARNESS_KIT + `
if (typeof TopK !== "function") {
  throw new Error("TopK is not defined. Keep the class name as given.");
}

function __feed(t, entries) {
  for (const [key, times] of entries) for (let i = 0; i < times; i++) t.add(key);
  return t;
}

const __tests = [
  {
    name: "returns the highest counts, highest first",
    about: "The basic contract.",
    run: function () {
      const t = __feed(new TopK(2), [["a", 3], ["b", 5], ["c", 1]]);
      __expectExact("top(2) of a:3 b:5 c:1", function () { return t.top(); },
        [{ key: "b", count: 5 }, { key: "a", count: 3 }]);
    }
  },
  {
    name: "counts repeated adds",
    about: "add records an occurrence; it does not set a value.",
    run: function () {
      const t = __feed(new TopK(1), [["x", 4]]);
      __expectExact("add x four times", function () { return t.top(); }, [{ key: "x", count: 4 }]);
    }
  },
  {
    name: "an empty stream returns nothing",
    about: "Nothing recorded is an empty list, not k entries of zero.",
    run: function () {
      __expectExact("top() with no adds", function () { return new TopK(3).top(); }, []);
    }
  },
  {
    name: "returns everything when fewer than k keys exist",
    hidden: true,
    about: "Padding to k, or refusing to answer until k keys exist, both break a dashboard on a quiet morning.",
    run: function () {
      const t = __feed(new TopK(5), [["a", 2], ["b", 1]]);
      __expectExact("top(5) with 2 keys", function () { return t.top(); },
        [{ key: "a", count: 2 }, { key: "b", count: 1 }]);
    }
  },
  {
    name: "ties break by key ascending",
    hidden: true,
    about: "Without a defined tie-break the output flickers between equal entries on every query, which looks like a bug to whoever is watching the dashboard.",
    run: function () {
      const t = __feed(new TopK(2), [["zebra", 5], ["apple", 5], ["mango", 1]]);
      __expectExact("top(2) with a tie at 5", function () { return t.top(); },
        [{ key: "apple", count: 5 }, { key: "zebra", count: 5 }]);
    }
  },
  {
    name: "a tie at the cut-off is resolved the same way",
    hidden: true,
    about: "The boundary is where a comparator that is right in the middle of the list goes wrong.",
    run: function () {
      const t = __feed(new TopK(2), [["a", 9], ["m", 4], ["b", 4]]);
      __expectExact("top(2), tie for second place", function () { return t.top(); },
        [{ key: "a", count: 9 }, { key: "b", count: 4 }]);
    }
  },
  {
    name: "reflects counts added after a query",
    hidden: true,
    about: "top() is a query, not a freeze. Caching the answer and never invalidating it is a stale dashboard.",
    run: function () {
      const t = __feed(new TopK(1), [["a", 2], ["b", 1]]);
      t.top();
      __feed(t, [["b", 5]]);
      __expectExact("query, then b overtakes a", function () { return t.top(); }, [{ key: "b", count: 6 }]);
    }
  },
  {
    name: "top() does not consume the data",
    hidden: true,
    about: "Draining a heap to produce the answer leaves the next query with less than it started with. Two identical queries must agree.",
    run: function () {
      const t = __feed(new TopK(2), [["a", 3], ["b", 2], ["c", 1]]);
      const first = t.top();
      const second = t.top();
      __expectExact("two identical queries agree", function () { return second; }, first);
      __expectExact("... and are still full length", function () { return second.length; }, 2);
    }
  },
  {
    name: "k of zero returns nothing",
    hidden: true,
    about: "A degenerate configuration should return nothing rather than crash or return everything.",
    run: function () {
      const t = __feed(new TopK(0), [["a", 3]]);
      __expectExact("top() with k=0", function () { return t.top(); }, []);
    }
  },
  {
    name: "correct across many keys",
    hidden: true,
    about: "Scale is where a partial-ordering shortcut stops agreeing with a full sort. This is the case a heap has to get right to be worth using.",
    run: function () {
      const t = new TopK(3);
      for (let i = 0; i < 200; i++) {
        const key = "k" + String(i).padStart(3, "0");
        for (let n = 0; n <= i % 17; n++) t.add(key);
      }
      const result = t.top();
      __expectExact("top(3) of 200 keys: length", function () { return result.length; }, 3);
      __assert("top(3) of 200 keys", result[0].count >= result[1].count && result[1].count >= result[2].count,
        ["results must be ordered by count, highest first", "but got " + __show(result)]);
      __expectExact("... the highest count present", function () { return result[0].count; }, 17);
    }
  },
  {
    name: "keys that look like numbers stay strings",
    hidden: true,
    about: "A plain object turns numeric-looking keys into integer properties and reorders them. That is a real difference when the keys are ids or status codes.",
    run: function () {
      const t = __feed(new TopK(2), [["404", 3], ["2", 5]]);
      __expectExact("numeric-looking keys", function () { return t.top(); },
        [{ key: "2", count: 5 }, { key: "404", count: 3 }]);
    }
  },
  {
    name: "a generated stream, checked against a plain sort",
    hidden: true,
    about: "A couple of hundred random keys, with the answer worked out a second time by counting everything and sorting it. The heap has to agree with the sort. Scale is where a partial ordering stops matching a full one, and the stream is different the next time this drill comes round, so there is nothing to remember.",
    run: function () {
      const g = __gen("stream");
      const k = g.int(2, 6);
      const alphabet = "abcdefgh".split("");
      const stream = [];
      const total = g.int(120, 240);
      for (let i = 0; i < total; i++) stream.push("/" + g.str(g.int(1, 3), alphabet));
      const t = new TopK(k);
      for (const key of stream) t.add(key);
      // The oracle counts and sorts the lot: obviously correct, and exactly the
      // O(n log n) the drill is asking you to beat.
      __expectOracle("top(" + k + ") after " + total + " generated adds",
        function () { return t.top(); },
        function () {
          const counts = new Map();
          for (const key of stream) counts.set(key, (counts.get(key) || 0) + 1);
          const all = [];
          counts.forEach(function (count, key) { all.push({ key: key, count: count }); });
          all.sort(function (a, b) {
            if (a.count !== b.count) return b.count - a.count;
            return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
          });
          return all.slice(0, k);
        });
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Sorting every key on every query is correct. Ask what that costs when there are two hundred thousand keys and the dashboard wants ten of them, once a second. Counting is the easy half; the query is the drill.",
    code: `class TopK {
  private counts = new Map<string, number>();

  constructor(private k: number) {}

  add(key: string): void {
    this.counts.set(key, (this.counts.get(key) ?? 0) + 1);
  }

  // top() is where the work is. A full sort is O(n log n) per query; keeping
  // only the k best while scanning is O(n log k).
}`,
  },
  {
    label: "Approach",
    body: "Scan the counts holding a heap of at most k. The heap keeps the WORST of the current best at its root, so \"does this one belong\" is a single comparison. One comparator defines both the heap order and the tie-break, so the two can never disagree.",
    code: `// Worse = smaller count, or on a tie the later key. Used by the heap AND by
// the final sort, so the tie-break at the cut-off matches the one in the list.
const worseThan = (a, b) => (a.count !== b.count ? a.count < b.count : a.key > b.key);

for (const [key, count] of this.counts) {
  const entry = { key, count };
  if (heap.length < this.k) {
    heap.push(entry);
    up(heap.length - 1);
  } else if (heap.length > 0 && worseThan(heap[0]!, entry)) {
    heap[0] = entry;      // beats the worst of the best
    down(0);
  }
}

// A heap is not itself ordered, so sort the k survivors on the way out.
// That is O(k log k), not O(n log n).
return heap.sort((a, b) => (b.count - a.count) || (a.key < b.key ? -1 : 1));`,
  },
  {
    label: "The trap",
    body: "Ties need a defined break or two equal entries swap places between queries and the dashboard looks broken, and the tie at the cut-off has to break the same way as the ties inside the list. And top() is a query: draining the heap to produce the answer leaves the next call with less than it started with, so two identical queries must agree.",
  },
];

export const topKStream: Drill = {
  id: "top-k-stream",
  track: "typescript",
  title: "Top K from a stream",
  pattern: "heap-topk",
  difficulty: "hard",
  basePoints: 300,
  timeBudgetMs: 30 * 60 * 1000,
  statement: `You are counting requests per endpoint and a dashboard asks, constantly, for the busiest ten. There are hundreds of thousands of distinct endpoints.

\`\`\`ts
class TopK {
  constructor(k: number);
  add(key: string): void;                              // record one occurrence
  top(): { key: string; count: number }[];             // the k highest, highest first
}
\`\`\`

## Requirements

- **\`add\` records an occurrence**, incrementing that key's count.
- **\`top\` returns at most k**, ordered by count descending.
- **Ties break by key ascending**, so the output is stable between queries.
- **Fewer than k keys** returns however many exist.
- **\`top\` is a query**: it does not consume anything, and two calls in a row agree.

## Example

\`\`\`ts
const busiest = new TopK(2);
["/api/users", "/api/users", "/health", "/api/users", "/health"].forEach((p) => busiest.add(p));

busiest.top();
// [{ key: "/api/users", count: 3 }, { key: "/health", count: 2 }]
\`\`\`

## Where this gets interesting

Sorting every key on every query is correct and is the answer that invites the follow-up: with 200,000 endpoints and a dashboard polling every second, you are sorting 200,000 items to show ten. Keeping only the best k while scanning is O(n log k), and the trick is that the heap holds the **smallest** of the current best at its root, so deciding whether a new entry belongs is one comparison.

The tie-break is not a nicety. Without a defined one, two entries on equal counts swap places between queries and the dashboard appears to flicker, which reads as a bug in the metrics rather than in the ordering.

One of the cases generates a couple of hundred adds and compares your answer against counting everything and sorting it, which is the O(n log n) version you are being asked to beat. The stream is different each time this drill comes round.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "add is O(1). top is O(n log k) over the distinct keys, plus O(k log k) to order the survivors. Beats O(n log n) whenever k is much smaller than n, which is the whole point.",
};
